/**
 * monthlyActualCostSSOTAdapter.js
 *
 * Thin Adapter over buildMonthlyActualCostData() (monthlyActualCost.js) +
 * its hierarchy layer (monthlyActualCostHierarchy.js). Those two files
 * are the Owner — this module reimplements NONE of their logic:
 *   - no source fetching (IPC/Payroll/Store/Petty Cash/Accrual)
 *   - no date normalization (actual_cost_date / issued_date / year+month)
 *   - no cost-code resolution (Sub Code -> Cost Code climbing)
 *   - no resource_type classification (resolveResourceType)
 *   - no indirect_category classification (resolveIndirectCategory)
 *
 * Same signature pattern as timeSeriesSSOTAdapter.js's
 * executeTimeSeriesQuery(projectId, forecastDates, query) — `periods`
 * (this dataset's own time axis: "YYYY-MM" strings the Owner's own rows
 * already carry, NOT a reformatting of forecast_history's forecastDate)
 * is a separate parameter, exactly like forecastDates is there.
 * query.time stays null/unused, matching the existing convention already
 * documented in analyticalQuery.js ("not used in Phase 1/2") — this
 * Adapter does not invent new meaning for it.
 *
 * Output is the SAME canonical TimeSeriesResult shape
 * executeTimeSeriesQuery() returns:
 *   { grain, series: [{ time, rows: [{ key, label, values }] }] }
 * so Bar.jsx/Line.jsx/visualContract.js consume it with ZERO changes —
 * they already read result.grain/result.series/row.values without any
 * knowledge of which Adapter produced them.
 *
 * Supported dimensions (v1, flat only — no hierarchical `groupings` tree
 * is ever exposed):
 *   null                — project-level (Owner's grandTotals)
 *   'cost_code'          — Owner's byCostCode (Level-1 Cost Code grain)
 *   'resource_type'       — Owner's groupings.resourceType.Direct (flat,
 *                            first level only — Direct rows only,
 *                            includes 'Unclassified' verbatim, never
 *                            dropped — see the Capability Audit this
 *                            Adapter was built after: this is
 *                            DELIBERATELY different from
 *                            cost_performance/time_series's
 *                            resourceTypeTotals, which only exposes 4
 *                            values and silently drops unclassified rows)
 *   'indirect_category'    — Owner's groupings.resourceType.Indirect
 *                            (== groupings.resourceCode.Indirect, same
 *                            object reference per the Owner's Single
 *                            Result rule — Indirect rows only, whatever
 *                            categories actually exist in
 *                            indirect_categories for this project, no
 *                            fixed/invented category list)
 *
 * Only measure in v1: 'actual'. `movement` (the Owner's own concept —
 * last-two-periods-present, not a calendar comparison) is not exposed —
 * see prior design note, unchanged from the original version of this
 * file.
 *
 * hasSnapshot is not meaningful for actual-cost transactions (there is
 * no "forecast fallback" concept here) — omitted entirely from each
 * series point, rather than set to a placeholder null that could be
 * misread as meaningful the way it is for time_series/s_curve.
 */

import { buildMonthlyActualCostData } from './monthlyActualCost'
import { validateQuery } from './analyticalQuery'

const SUPPORTED_DIMENSIONS = [null, 'cost_code', 'resource_type', 'indirect_category', 'sub_code', 'resource_code']

// ─── Multi-select Filter Contract (approved, same rules as
// financialSSOTAdapter.js — not re-documented in full here) ────────────────
function matchesFilter(value, filterValue) {
  if (filterValue == null || filterValue === 'all') return true
  if (Array.isArray(filterValue)) return filterValue.includes(value)
  return value === filterValue
}
// Resolves the set of allowed values for one filter key against this
// Owner's groupings object (e.g. groupings.resourceType.Direct or
// .Indirect) — a single value or array both resolve to a UNION of
// costCodes across every matching group key.
function resolveAllowedCostCodesFromGrouping(groupingObj, filterValue) {
  if (filterValue == null || filterValue === 'all') return null // no restriction
  const keys = Array.isArray(filterValue) ? filterValue : [filterValue]
  const allowed = new Set()
  for (const key of keys) {
    const group = groupingObj[key]
    if (group) Object.keys(group.costCodes).forEach(c => allowed.add(c))
  }
  return allowed
}

// FILTER CONTRACT (Phase 6A, approved; extended for Multi-select in this
// Stage) — different mechanics from financialSSOTAdapter.js/
// timeSeriesSSOTAdapter.js, because this Owner's data shape is
// different: buildHierarchyFromRows() (monthlyActualCostHierarchy.js)
// already groups rows as <resource_type or indirect_category> →
// costCode → subCode → [rows], so the Cost Code membership for a given
// resource_type/indirect_category is ALREADY the hierarchy's own
// structure — no existence-check reconstruction needed, unlike
// financialSSOTAdapter.js's resourceRows.some(...) workaround. This is
// still a membership filter, not a measure filter: filtering
// dimension='cost_code' by resource_type='material' means "show only
// Cost Codes that have a 'material' entry in
// groupings.resourceType.Direct.material.costCodes" — using each
// filtered Cost Code's FULL byCostCode total (all its resource types
// combined), never re-aggregated down to just the Material portion,
// identical posture to the other two Adapters' Extension.
function getFilteredCostCodes(data, filters) {
  let allCostCodes = Object.keys(data.byCostCode)

  if (filters?.cost_code != null) {
    // Direct equality/membership on the Cost Code key itself — simplest
    // of all filters here, same posture as financialSSOTAdapter.js's
    // equivalent addition.
    allCostCodes = allCostCodes.filter(c => matchesFilter(c, filters.cost_code))
  }

  if (filters?.resource_type == null && filters?.indirect_category == null) return allCostCodes

  let allowedSet = null
  const rtAllowed = resolveAllowedCostCodesFromGrouping(data.groupings.resourceType.Direct, filters?.resource_type)
  if (rtAllowed) allowedSet = rtAllowed

  const icAllowed = resolveAllowedCostCodesFromGrouping(data.groupings.resourceType.Indirect, filters?.indirect_category)
  if (icAllowed) {
    // Both filters present: intersection (a Cost Code must satisfy BOTH)
    allowedSet = allowedSet ? new Set([...allowedSet].filter(c => icAllowed.has(c))) : icAllowed
  }

  return allowedSet ? allCostCodes.filter(c => allowedSet.has(c)) : allCostCodes
}

/**
 * @param {string} projectId
 * @param {string[]} periods - "YYYY-MM" strings to include in the
 *   result, e.g. from buildMonthlyActualCostData()'s own `periods`
 *   output. This Adapter does not discover periods itself — same
 *   division of responsibility as executeTimeSeriesQuery(), which does
 *   not decide which forecastDates to use either.
 * @param {AnalyticalQuery} query
 * @returns {Promise<{grain: string, series: Array<{time: string, rows: Array<{key,label,values}>}>}>}
 */
export async function executeMonthlyActualCostQuery(projectId, periods, query) {
  const { valid, errors } = validateQuery(query)
  if (!valid) throw new Error(`Invalid query: ${errors.join('; ')}`)
  if (query.dataset !== 'monthly_actual_cost') {
    throw new Error(
      `Dataset "${query.dataset}" is not handled by monthlyActualCostSSOTAdapter — ` +
      `only "monthly_actual_cost" is wired up here.`
    )
  }
  if (!SUPPORTED_DIMENSIONS.includes(query.dimension)) {
    throw new Error(
      `monthlyActualCostSSOTAdapter: dimension "${query.dimension}" is not supported in v1 — ` +
      `only ${SUPPORTED_DIMENSIONS.map(d => d ?? 'null').join(', ')} are available.`
    )
  }

  const data = await buildMonthlyActualCostData(projectId)

  // periods requested but absent from the Owner's own data are still
  // included in the series — same "allowed, not an error" posture as
  // timeSeriesSSOTAdapter's handling of a forecastDate with no matching
  // snapshot (Phase 4/5A precedent) — every requested point appears,
  // with 0/empty values rather than being silently dropped.
  const requestedPeriods = (periods && periods.length > 0) ? periods : data.periods

  if (requestedPeriods.length === 0) {
    return { grain: query.dimension || 'project', series: [] }
  }

  // ── dimension: null (project-level) ──────────────────────────────────
  if (query.dimension === null) {
    const series = requestedPeriods.map(p => ({
      time: p,
      rows: [{
        key: projectId,
        label: 'Project',
        values: { actual: data.grandTotals[p] || 0 },
      }],
    }))
    return { grain: 'project', series }
  }

  // ── dimension: 'cost_code' ───────────────────────────────────────────
  if (query.dimension === 'cost_code') {
    const allowedCodes = getFilteredCostCodes(data, query.filters)
    const entries = allowedCodes.map(code => [code, data.byCostCode[code]])
    const series = requestedPeriods.map(p => ({
      time: p,
      rows: entries.map(([code, ccData]) => ({
        key: code,
        label: ccData.desc || code,
        values: { actual: ccData.periodTotals[p] || 0 },
      })),
    }))
    return { grain: 'cost_code', series }
  }

  // ── dimension: 'resource_type' (Direct only, includes Unclassified) ──
  if (query.dimension === 'resource_type') {
    const entries = Object.entries(data.groupings.resourceType.Direct)
    const series = requestedPeriods.map(p => ({
      time: p,
      rows: entries.map(([resourceType, groupData]) => ({
        key: resourceType,
        label: resourceType,
        values: { actual: groupData.periodTotals[p] || 0 },
      })),
    }))
    return { grain: 'resource_type', series }
  }

  // ── dimension: 'indirect_category' (Indirect only, real categories) ─
  if (query.dimension === 'indirect_category') {
    const entries = Object.entries(data.groupings.resourceType.Indirect)
    const series = requestedPeriods.map(p => ({
      time: p,
      rows: entries.map(([category, groupData]) => ({
        key: category,
        label: category,
        values: { actual: groupData.periodTotals[p] || 0 },
      })),
    }))
    return { grain: 'indirect_category', series }
  }

  // ── dimension: 'sub_code' (flat — every Sub Code across all Cost
  //    Codes under BOTH Direct and Indirect groupings combined) ────────
  // Reads groupings.resourceType.<Direct|Indirect>.<type>.costCodes.
  // <code>.subCodes — the Level-3 grouping confirmed to exist in
  // buildHierarchyFromRows() (monthlyActualCostHierarchy.js, ~line 265:
  // "Level 3 grouping: <classifyFn result> -> costCode -> subCode ->
  // [rows]"), with real periodTotals/total/movement per Sub Code, not
  // hierarchy-only metadata. filters (resource_type/indirect_category)
  // still apply via getFilteredCostCodes — a Sub Code is only included
  // if its parent Cost Code passes the same membership filter used for
  // dimension='cost_code'.
  if (query.dimension === 'sub_code') {
    const allowedCodes = new Set(getFilteredCostCodes(data, query.filters))
    const subCodeRows = []
    for (const groupingRoot of [data.groupings.resourceType.Direct, data.groupings.resourceType.Indirect]) {
      for (const groupData of Object.values(groupingRoot)) {
        for (const [costCode, ccData] of Object.entries(groupData.costCodes)) {
          if (!allowedCodes.has(costCode)) continue
          for (const [subCode, subData] of Object.entries(ccData.subCodes || {})) {
            subCodeRows.push({ key: subCode, label: `${subCode} (${ccData.desc || costCode})`, data: subData })
          }
        }
      }
    }
    const series = requestedPeriods.map(p => ({
      time: p,
      rows: subCodeRows.map(({ key, label, data: subData }) => ({
        key, label, values: { actual: subData.periodTotals[p] || 0 },
      })),
    }))
    return { grain: 'sub_code', series }
  }

  // ── dimension: 'resource_code' (flat — every Resource Code, same
  //    membership-filtered set as sub_code above but from
  //    groupings.resourceCode instead of groupings.resourceType) ───────
  // groupings.resourceCode has the identical shape to groupings.resourceType
  // (same buildHierarchyFromRows() builder, different classifyFn —
  // resolveResourceDictCode instead of resolveResourceType — confirmed
  // in Stage 1's Final Gate). Direct/Indirect split works the same way.
  const allowedCodes = new Set(getFilteredCostCodes(data, query.filters))
  const resourceCodeRows = []
  for (const groupingRoot of [data.groupings.resourceCode.Direct, data.groupings.resourceCode.Indirect]) {
    for (const [resourceCode, groupData] of Object.entries(groupingRoot)) {
      for (const [costCode, ccData] of Object.entries(groupData.costCodes)) {
        if (!allowedCodes.has(costCode)) continue
        resourceCodeRows.push({
          key: `${resourceCode}::${costCode}`,
          label: `${resourceCode} (${ccData.desc || costCode})`,
          data: ccData,
        })
      }
    }
  }
  const series = requestedPeriods.map(p => ({
    time: p,
    rows: resourceCodeRows.map(({ key, label, data: ccData }) => ({
      key, label, values: { actual: ccData.periodTotals[p] || 0 },
    })),
  }))
  return { grain: 'resource_code', series }
}
